import { useEffect, useRef, useState, useCallback } from 'react';
import { RotateCcw, X, Check } from 'lucide-react';
import { showToast } from './Toast';

const DEFAULT_CENTER = [42.408, -71.03];
const DEFAULT_ZOOM = 12;

// Create marker icon with store number
const createStoreMarkerIcon = (numero, isAVisitar = false) => {
  // Red for "a_visitar", blue/cyan for "visitada"
  const bgColor = isAVisitar ? '#ef4444' : '#0288d1';
  const innerColor = isAVisitar ? '#dc2626' : '#0288d1';
  
  // Format number (pad with 0 if single digit)
  const displayNum = numero < 10 ? `0${numero}` : `${numero}`;
  const fontSize = displayNum.length > 2 ? 11 : 13;
  
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
      <defs>
        <filter id="sh-store-${numero}">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#0005"/>
        </filter>
      </defs>
      <path d="M20 0C11.16 0 4 7.16 4 16c0 10.5 16 34 16 34S36 26.5 36 16C36 7.16 28.84 0 20 0z" fill="${bgColor}" filter="url(#sh-store-${numero})"/>
      <circle cx="20" cy="16" r="14" fill="#fff" opacity=".95"/>
      <text x="20" y="21" text-anchor="middle" font-size="${fontSize}" font-weight="bold" fill="${innerColor}">${displayNum}</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svgIcon)}`;
};

// Create numbered marker icon for clusters (multiple stores at same location)
const createNumberedMarkerIcon = (count, isAVisitar = false) => {
  const bgColor = isAVisitar ? '#ef4444' : '#0288d1';
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
      <defs>
        <filter id="sh-num-${count}">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#0005"/>
        </filter>
      </defs>
      <path d="M20 0C11.16 0 4 7.16 4 16c0 10.5 16 34 16 34S36 26.5 36 16C36 7.16 28.84 0 20 0z" fill="${bgColor}" filter="url(#sh-num-${count})"/>
      <circle cx="20" cy="16" r="14" fill="#fff" opacity=".95"/>
      <text x="20" y="21" text-anchor="middle" font-size="14" font-weight="bold" fill="${bgColor}">${count}</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svgIcon)}`;
};

export function MapaTab({ lojas, pinMode, onPinConfirm, onCancelPin, focusLoja }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [L, setL] = useState(null);

  // Load Leaflet dynamically
  useEffect(() => {
    const loadLeaflet = async () => {
      // Check if already loaded
      if (window.L) {
        setL(window.L);
        return;
      }

      // Load CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
        document.head.appendChild(link);
      }

      // Load JS
      if (!document.querySelector('script[src*="leaflet"]')) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
        script.async = true;
        script.onload = () => {
          setL(window.L);
        };
        document.head.appendChild(script);
      }
    };

    loadLeaflet();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!L || !mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true
    });

    // OSM tiles with CartoDB fallback
    let tileFailures = 0;
    const osmTiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    });

    osmTiles.on('tileerror', () => {
      tileFailures++;
      if (tileFailures >= 3 && map.hasLayer(osmTiles)) {
        map.removeLayer(osmTiles);
        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          attribution: '© OpenStreetMap contributors, © CARTO',
          maxZoom: 19
        }).addTo(map);
        showToast('Usando mapa alternativo', 'warning');
      }
    });

    osmTiles.addTo(map);
    mapRef.current = map;
    setIsMapReady(true);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [L]);

  // Handle pin mode click
  useEffect(() => {
    if (!mapRef.current || !L) return;

    const handleMapClick = (e) => {
      if (pinMode) {
        setPendingPin({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    };

    mapRef.current.on('click', handleMapClick);

    return () => {
      mapRef.current?.off('click', handleMapClick);
    };
  }, [pinMode, L]);

  // Update markers when lojas change
  useEffect(() => {
    if (!mapRef.current || !L || !isMapReady) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Log all lojas for debugging
    console.log(`[Map] Total lojas received: ${lojas.length}`);
    const visitadas = lojas.filter(l => l.status !== 'a_visitar');
    const aVisitar = lojas.filter(l => l.status === 'a_visitar');
    console.log(`[Map] Visitadas: ${visitadas.length}, A Visitar: ${aVisitar.length}`);
    
    lojas.forEach((l, idx) => {
      const storeNum = l.numero || (idx + 1);
      console.log(`[Map] Loja #${storeNum}: "${l.name}" | Status: ${l.status} | Lat: ${l.lat} | Lng: ${l.lng}`);
    });

    // Group lojas by similar coordinates (within ~50 meters)
    const PROXIMITY_THRESHOLD = 0.0005; // roughly 50 meters
    const groups = [];
    
    lojas.forEach(loja => {
      const lat = parseFloat(loja.lat);
      const lng = parseFloat(loja.lng);
      
      if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
        console.log(`[Map] Skipping loja "${loja.name}" - invalid coordinates (lat: ${loja.lat}, lng: ${loja.lng})`);
        return;
      }
      
      // Find existing group with similar coordinates
      const existingGroup = groups.find(g => 
        Math.abs(g.lat - lat) < PROXIMITY_THRESHOLD && 
        Math.abs(g.lng - lng) < PROXIMITY_THRESHOLD
      );
      
      if (existingGroup) {
        existingGroup.lojas.push(loja);
      } else {
        groups.push({ lat, lng, lojas: [loja] });
      }
    });

    console.log(`[Map] Created ${groups.length} marker groups from ${lojas.length} lojas`);

    // Create markers for each group
    groups.forEach(group => {
      const { lat, lng, lojas: groupLojas } = group;
      const count = groupLojas.length;
      const hasAVisitar = groupLojas.some(l => l.status === 'a_visitar');
      
      // Create popup content listing all lojas in this location
      const popupContent = groupLojas.map(loja => {
        const isAVisitar = loja.status === 'a_visitar';
        // Get store number - use existing numero or find index in full lojas array
        const storeNum = loja.numero || (lojas.findIndex(l => l.id === loja.id) + 1);
        const displayNum = storeNum < 10 ? `0${storeNum}` : `${storeNum}`;
        const numBadge = `<span style="background:${isAVisitar ? '#ef4444' : '#0288d1'};color:#fff;padding:2px 8px;border-radius:6px;font-size:11px;font-weight:bold;margin-right:6px;">#${displayNum}</span>`;
        const statusBadge = isAVisitar 
          ? '<span style="background:#fee2e2;color:#dc2626;padding:2px 6px;border-radius:10px;font-size:10px;font-weight:bold;">A VISITAR</span>'
          : '<span style="background:#dcfce7;color:#16a34a;padding:2px 6px;border-radius:10px;font-size:10px;font-weight:bold;">VISITADA</span>';
        
        const sellersHtml = loja.sellers?.length > 0
          ? `<div style="margin-top:4px;font-size:11px;">${loja.sellers.map(s => 
              `<a href="tel:${s.phone?.replace(/\D/g, '')}" style="color:#0288d1;text-decoration:none;">${s.name}</a>`
            ).join(' · ')}</div>`
          : '';

        return `
          <div style="padding:8px 0;border-bottom:1px solid #eee;">
            <div style="display:flex;align-items:center;gap:4px;margin-bottom:4px;flex-wrap:wrap;">
              ${numBadge}
              <strong style="font-size:13px;color:#1a1a2e;">${loja.name}</strong>
              ${statusBadge}
            </div>
            <p style="margin:2px 0;color:#666;font-size:11px;">
              ${loja.address || ''}${loja.address ? ', ' : ''}${loja.city}, ${loja.state}
            </p>
            ${loja.manager ? `<p style="margin:2px 0;font-size:11px;"><strong>Manager:</strong> ${loja.manager}</p>` : ''}
            ${sellersHtml}
          </div>
        `;
      }).join('');

      const headerText = count > 1 
        ? `<div style="font-weight:bold;font-size:14px;color:#0288d1;margin-bottom:8px;padding-bottom:8px;border-bottom:2px solid #0288d1;">${count} Stores at this location</div>`
        : '';

      const fullPopupContent = `
        <div style="max-width:280px;max-height:300px;overflow-y:auto;font-family:-apple-system,sans-serif;">
          ${headerText}
          ${popupContent}
        </div>
      `;

      // Create icon based on count
      let icon;
      if (count > 1) {
        // Multiple stores at same location - show count
        const iconUrl = createNumberedMarkerIcon(count, hasAVisitar);
        icon = L.icon({
          iconUrl,
          iconSize: [40, 50],
          iconAnchor: [20, 50],
          popupAnchor: [0, -50]
        });
      } else {
        // Single store - show store number
        const loja = groupLojas[0];
        const isAVisitar = loja.status === 'a_visitar';
        // Get store number
        const storeNum = loja.numero || (lojas.findIndex(l => l.id === loja.id) + 1);
        
        const iconUrl = createStoreMarkerIcon(storeNum, isAVisitar);
        icon = L.icon({
          iconUrl,
          iconSize: [40, 50],
          iconAnchor: [20, 50],
          popupAnchor: [0, -50]
        });
      }

      const marker = L.marker([lat, lng], { icon })
        .addTo(mapRef.current)
        .bindPopup(fullPopupContent);

      markersRef.current.push(marker);
    });
  }, [lojas, L, isMapReady]);

  // Focus on specific loja
  useEffect(() => {
    if (!mapRef.current || !focusLoja || !focusLoja.lat || !focusLoja.lng) return;

    mapRef.current.setView([focusLoja.lat, focusLoja.lng], 16);

    // Open popup of the focused marker
    const marker = markersRef.current.find(m => {
      const pos = m.getLatLng();
      return Math.abs(pos.lat - focusLoja.lat) < 0.0001 && Math.abs(pos.lng - focusLoja.lng) < 0.0001;
    });

    if (marker) {
      marker.openPopup();
    }
  }, [focusLoja]);

  const handleReset = () => {
    if (mapRef.current) {
      mapRef.current.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }
    if (pinMode) {
      onCancelPin();
    }
    setPendingPin(null);
  };

  const handleConfirmPin = () => {
    if (pendingPin) {
      onPinConfirm(pendingPin.lat, pendingPin.lng);
      setPendingPin(null);
    }
  };

  const handleCancelPendingPin = () => {
    setPendingPin(null);
  };

  return (
    <div className="relative h-[calc(100vh-8rem)] w-full rounded-xl overflow-hidden border border-[var(--border-color)] shadow-inner">
      {/* Pin Mode Banner */}
      {pinMode && (
        <div 
          data-testid="pin-mode-banner"
          className="absolute top-0 left-0 right-0 bg-purple-600 text-white py-2 px-4 z-[1000] flex items-center justify-center gap-2 text-sm font-medium"
        >
          <span>📌</span> Pin Mode — Tap on the map to set location
          <button
            data-testid="btn-cancel-pin-mode"
            onClick={onCancelPin}
            className="ml-auto p-1 hover:bg-white/20 rounded"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Map Container */}
      <div 
        ref={mapContainerRef} 
        data-testid="map-container"
        className="w-full h-full bg-[var(--bg-chip)]"
      />

      {/* Reset Button */}
      <button
        data-testid="btn-reset-map"
        onClick={handleReset}
        className="absolute top-4 right-4 z-[1000] p-2 bg-[var(--bg-card)] rounded-lg shadow-md border border-[var(--border-color)] active:opacity-80 transition-colors"
        style={{ top: pinMode ? '48px' : '16px' }}
      >
        <RotateCcw size={20} className="text-[var(--text-secondary)]" />
      </button>

      {/* Pin Confirmation Modal */}
      {pendingPin && (
        <div className="absolute inset-0 bg-black/40 z-[1001] flex items-center justify-center p-4">
          <div 
            data-testid="pin-confirm-modal"
            className="bg-[var(--bg-card)] rounded-xl p-6 shadow-xl max-w-sm w-full border border-[var(--border-color)]"
          >
            <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2">
              Confirm location here?
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Lat: {pendingPin.lat.toFixed(6)}<br/>
              Lng: {pendingPin.lng.toFixed(6)}
            </p>
            <div className="flex gap-3">
              <button
                data-testid="btn-confirm-pin"
                onClick={handleConfirmPin}
                className="flex-1 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-green-700 font-medium"
              >
                <Check size={18} /> Yes
              </button>
              <button
                data-testid="btn-cancel-pin"
                onClick={handleCancelPendingPin}
                className="flex-1 h-10 bg-[var(--bg-chip)] text-[var(--text-primary)] rounded-lg flex items-center justify-center gap-2 active:opacity-80 font-medium border border-[var(--border-color)]"
              >
                <X size={18} /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {!isMapReady && (
        <div className="absolute inset-0 bg-[var(--bg-chip)] flex items-center justify-center">
          <div className="text-[var(--text-muted)] text-sm">Loading map...</div>
        </div>
      )}
    </div>
  );
}
